/* ps-load-more.js -- a "Load more" button for Impulse collection pages.
 *
 * WHY THIS EXISTS. Abdo paid for the ScrollerPro app to get infinite scroll and
 * removed it on 2026-08-17, because it was hiding products: it deleted Impulse's
 * numbered pagination from the DOM unconditionally, and reset its own page
 * counter only inside blocks hardcoded to two OTHER stores' domains
 * (larriet.com.au, wildrosevintage.ca). Impulse re-renders the grid on every
 * filter, so the counter desynced -- scroll to page 3, filter, and the next
 * scroll requested page 4. Pages 2 and 3 became unreachable, with no fallback
 * because the pagination was already gone.
 *
 * Impulse has NO native load-more: `loadMore`, `infinite` and `paginate` all
 * appear ZERO times in its 162,794 B theme.js. Verified, not assumed.
 *
 * THE TWO RULES THAT SHAPE EVERYTHING BELOW, both learned from that app:
 *
 *   1. NEVER REMOVE THE PAGINATION FROM THE DOM. It is hidden with a class, so
 *      the <a href="?page=2"> links stay in the HTML and Google keeps crawling
 *      every page. Deleting them is what made ScrollerPro's bug unrecoverable.
 *   2. NEVER TRACK PAGE NUMBERS. There is no counter in this file. The next URL
 *      is read out of the freshly-fetched document's own pagination block every
 *      single time. A counter is a second source of truth that can desync from
 *      the page; a link cannot.
 *
 * NO BUILD STEP, NO MAP, NO GENERATOR. Impulse ships no jQuery (confirmed), so
 * this is vanilla and self-contained.
 */
(function () {
  "use strict";

  var AJAX = "#CollectionAjaxContent";
  var BUSY = "ps-loadmore--busy";
  var HIDE = "ps-pagination--replaced";

  /* Locale from lang + pathname, NEVER document.dir. The Arabic pages ship
     <html lang="ar" dir="ltr"> -- a known live defect -- so reading dir would
     class every Arabic shopper as English. Same approach as
     ps-collection-chips.js, deliberately, so the two cannot disagree. */
  function isArabic() {
    var lang = (document.documentElement.getAttribute("lang") || "").toLowerCase();
    return lang.indexOf("ar") === 0 || /^\/ar(\/|$)/.test(window.location.pathname);
  }

  /* "عرض المزيد" is the standard Egyptian storefront wording for this control.
     FLAGGED FOR arabic-register: it is the one string in this file that is
     store copy rather than mechanism. If it is ruled wrong, this is the only
     line to change. */
  function labels() {
    return isArabic()
      ? { more: "عرض المزيد", loading: "جاري التحميل..." }
      : { more: "Load more",  loading: "Loading..." };
  }

  /* Impulse's product grid is the PARENT of the first .grid-product, not a
     class we hard-code. Read from the live markup: the grid carries
     `grid grid--uniform small--grid--flush`, but a theme update could change
     those modifiers -- it cannot change the parent-child relationship. */
  function gridIn(root) {
    var first = root.querySelector(".grid-product");
    return first ? first.parentElement : null;
  }

  function paginationIn(root) {
    return root.querySelector(".pagination");
  }

  /* The next-page URL, straight from the document being read. Returns null on
     the last page, which is how the button knows to remove itself. */
  function nextHrefIn(root) {
    var pag = paginationIn(root);
    if (!pag) return null;
    var a = pag.querySelector(".next a[href]");
    return a ? a.getAttribute("href") : null;
  }

  /* THE BUG THIS FIXES, because it cost Abdo a test round on 2026-08-17:
     appended product images did not appear at all. Impulse hides them until
     AOS marks them visible --

       .aos-animate[data-aos=image-fade-in] .image-element {
         animation: fade-in 0.5s cubic-bezier(0.29,0.65,0.58,1) forwards; }

     -- and AOS snapshots the DOM when the page loads, so it never observes a
     card appended afterwards. No .aos-animate, no animation, and the image
     stays at its hidden start state permanently. The product looked missing.

     WHY THE CLASS IS SET DIRECTLY RATHER THAN ASKING AOS TO RESCAN. AOS.refreshHard()
     is called too (below, defensively) but it is NOT the fix: it is a global
     re-scan of every animated element on the page, it depends on window.AOS
     existing, and if a theme update drops AOS the images silently vanish again.
     Setting the class is local, synchronous, and correct even with no AOS at all.

     Applied to the node AND its descendants: data-aos sits on <image-element>
     inside the card, not on the card itself, so a node-only pass would miss
     every image. */
  function reveal(node) {
    if (node.hasAttribute && node.hasAttribute("data-aos")) {
      node.classList.add("aos-animate");
    }
    var animated = node.querySelectorAll ? node.querySelectorAll("[data-aos]") : [];
    for (var i = 0; i < animated.length; i++) {
      animated[i].classList.add("aos-animate");
    }
    /* Impulse's ImageElement marks its own wrapper loaded; a node cloned from a
       DOMParser document has not run that path, so do it here too. */
    var wraps = node.querySelectorAll ? node.querySelectorAll(".image-wrap") : [];
    for (var j = 0; j < wraps.length; j++) {
      wraps[j].classList.add("loaded");
    }
  }

  var selfDispatch = false;   /* guards the collection:reloaded round-trip */

  function build(container) {
    var grid = gridIn(container);
    var pag = paginationIn(container);
    var next = nextHrefIn(container);

    /* No grid, no pagination, or already on the last page -> nothing to do.
       Leave the DOM exactly as Impulse rendered it. */
    if (!grid || !pag || !next) return;

    var text = labels();
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "btn ps-loadmore";
    btn.textContent = text.more;
    btn.setAttribute("data-next", next);

    /* The pagination is HIDDEN, never removed -- rule 1 above. */
    pag.classList.add(HIDE);
    pag.parentNode.insertBefore(btn, pag);

    btn.addEventListener("click", function () {
      if (btn.classList.contains(BUSY)) return;      /* double-tap guard */
      var href = btn.getAttribute("data-next");
      if (!href) return;

      btn.classList.add(BUSY);
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");
      btn.textContent = text.loading;

      fetch(href, { credentials: "same-origin" })
        .then(function (r) {
          if (!r.ok) throw new Error("HTTP " + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, "text/html");
          var fetched = doc.querySelector(AJAX);
          if (!fetched) throw new Error("no " + AJAX + " in the fetched page");

          var added = [];
          var incoming = fetched.querySelectorAll(".grid-product");
          for (var i = 0; i < incoming.length; i++) {
            /* Append the grid ITEM, not .grid-product__content -- the item
               carries the column classes that make the grid a grid. */
            var node = document.importNode(incoming[i], true);
            grid.appendChild(node);
            reveal(node);
            added.push(node);
          }

          /* Rule 2: the new next URL comes from the fetched document itself. */
          var after = nextHrefIn(fetched);
          if (after) {
            btn.setAttribute("data-next", after);
            btn.classList.remove(BUSY);
            btn.disabled = false;
            btn.removeAttribute("aria-busy");
            btn.textContent = text.more;
          } else {
            /* Last page reached. The button goes; the hidden pagination stays
               hidden, because there is nothing left to fall back to. */
            btn.parentNode.removeChild(btn);
          }

          /* Let the rest of the store decorate what just arrived.
             ps-collection-chips.js listens for exactly this event, so newly
             appended cards get their size chips. The flag stops our own
             listener from treating this as a filter re-render and rebuilding
             the button underneath itself. */
          /* Defensive only. reveal() has already made the images visible;
             this keeps AOS's own bookkeeping consistent for anything else on
             the page that depends on it. Guarded, because AOS may not exist. */
          if (window.AOS && typeof window.AOS.refreshHard === "function") {
            try { window.AOS.refreshHard(); } catch (e) {}
          }

          selfDispatch = true;
          try {
            document.dispatchEvent(new CustomEvent("collection:reloaded"));
          } finally {
            selfDispatch = false;
          }

          /* Move focus to the first new product so a keyboard user is not
             dropped back at the top. No announcement string -- that would be
             invented Arabic copy, and focus does the job without any. */
          if (added.length) {
            var link = added[0].querySelector("a[href]");
            if (link) {
              link.setAttribute("tabindex", "-1");
              link.focus({ preventScroll: true });
            }
          }
        })
        .catch(function () {
          /* THE FALLBACK THAT SCROLLERPRO DID NOT HAVE. On any failure the
             numbered pagination comes back and the button goes away, so the
             shopper can always still reach page 2 by tapping a number. A
             broken enhancement must degrade to the thing it enhanced. */
          pag.classList.remove(HIDE);
          if (btn.parentNode) btn.parentNode.removeChild(btn);
        });
    });
  }

  function init() {
    var container = document.querySelector(AJAX);
    if (container) build(container);
  }

  /* Impulse replaces CollectionAjaxContent's innerHTML on every filter and
     sort, which destroys the button. Its own `collection:reloaded` event is the
     hook for rebuilding -- the same event this file dispatches after appending,
     hence the guard. */
  document.addEventListener("collection:reloaded", function () {
    if (selfDispatch) return;
    init();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
